import { loadConfig } from "../config/appConfig";


export const authenticatedFetch = async (endpoint, options = {}) => {
  try {
    const rota = await loadConfig();

    const url = `${rota.API_URL}${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include'
    });

    if (response.status === 401) {
      window.location.href = '/login';
      return new Promise(() => { });
    }

    return response;
  } catch (error) {
    if (error.response?.status >= 500) {
      notificationService.error("Erro no servidor. Tente novamente mais tarde.");
    }
    throw error;
  }
};

export const get = async (endpoint) => {
  const response = await authenticatedFetch(endpoint, {
    method: 'GET'
  });

  const text = await response.clone().text();
  const contentType = response.headers.get('Content-Type');

  const responseBody =
    contentType && contentType.includes('application/json')
      ? JSON.parse(text)
      : text;

  if (!response.ok) {
    const error = new Error(responseBody.message || responseBody.error || `Erro: ${response.status}`);
    error.response = {
      status: response.status,
      data: responseBody
    };
    throw error;
  }

  return responseBody;
};

export const post = async (endpoint, data) => {
  const response = await authenticatedFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(data)
  });

  const contentType = response.headers.get('Content-Type');
  const responseBody = contentType && contentType.includes('application/json')
    ? await response.json().catch(() => ({}))
    : await response.text().catch(() => '');

  if (!response.ok) {
    const error = new Error(responseBody.message || responseBody.error || `Erro: ${response.status}`);
    error.response = { status: response.status, data: responseBody };
    throw error;
  }

  return responseBody;
};

export const put = async (endpoint, data) => {
  const response = await authenticatedFetch(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json'
    }
  });

  const contentType = response.headers.get('Content-Type');
  const responseBody = contentType && contentType.includes('application/json')
    ? await response.json().catch(() => ({}))
    : await response.text().catch(() => '');

  if (!response.ok) {
    const error = new Error(responseBody.message || responseBody.error || `Erro: ${response.status}`);
    error.response = { status: response.status, data: responseBody };
    throw error;
  }

  return responseBody;
};


export const patch = async (endpoint, data) => {
  const response = await authenticatedFetch(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json'
    }
  });

  const contentType = response.headers.get('Content-Type');
  const responseBody = contentType && contentType.includes('application/json')
    ? await response.json().catch(() => ({}))
    : await response.text().catch(() => '');

  if (!response.ok) {
    const error = new Error(responseBody.message || responseBody.error || `Erro: ${response.status}`);
    error.response = { status: response.status, data: responseBody };
    throw error;
  }

  return responseBody;
};

export const del = async (endpoint) => {
  const response = await authenticatedFetch(endpoint, {
    method: 'DELETE'
  });

  if (response.status === 204) {
    return { success: true };
  }

  const contentType = response.headers.get('Content-Type');
  const responseBody = contentType && contentType.includes('application/json')
    ? await response.json().catch(() => ({}))
    : await response.text().catch(() => '');

  if (!response.ok) {
    const error = new Error(responseBody.message || responseBody.error || `Erro: ${response.status}`);
    error.response = { status: response.status, data: responseBody };
    throw error;
  }

  return responseBody;
};

export const api = {
  telemetry: {
    getAlerts: () => get('/api/telemetry/alerts'),
    monitor: (trainId, bloco, minutes = 10) =>
      get(`/api/telemetry/trem/${trainId}/monitor?bloco=${bloco}&minutes=${minutes}`),
    getAll: () => get('/api/telemetry/all'),
    search: (params) => {
      const query = new URLSearchParams(
        Object.entries(params).filter(([_, v]) => v !== null && v !== "" && v !== undefined)
      ).toString();

      return get(`/api/telemetry/search?${query}`);
    },
    getById: (id) => get(`/api/telemetry/${id}`),
    getCars: (id) => get(`/api/telemetry/${id}/cars`),
    getCar: (trainId, carId) => get(`/api/telemetry/${trainId}/cars/${carId}`)
  },

  usuario: {
    getAll: (page, pageSize) => get(`/api/usuario?page=${page}&pageSize=${pageSize}`),
    getById: (id) => get(`/api/usuario/${id}`),
    getEmails: () => get('/api/usuario/emails'),
    verifyEmail: (email) => get(`/api/usuario/verify-email/${encodeURIComponent(email)}`),
    create: (data) => post('/api/usuario', data),
    update: (id, data) => put(`/api/usuario/${id}`, data),
    delete: (id) => del(`/api/usuario/${id}`)
  },

  roles: {
    getAll: () => get('/api/roles')
  },

  trem: {
    getAll: (page, pageSize) => get(`/api/trem?page=${page}&pageSize=${pageSize}`),
    getById: (id) => get(`/api/trem/${id}`),
    create: (data) => post('/api/trem', data),
    update: (id, data) => put(`/api/trem/${id}`, data),
    alocar: (id, data) => patch(`/api/trem/${id}/alocacao`, data),
    delete: (id) => del(`/api/trem/${id}`)
  },

  frota: {
    getAll: () => get('/api/frotas')
  },

  linhas: {
    getAll: () => get('/api/linha')
  },

  status: {
    getAll: () => get('/api/status')
  },

  auth: {
    checkAuth: async () => {
      const isAuth = localStorage.getItem('isAuthenticated');
      const userInfo = localStorage.getItem('userInfo');

      if (isAuth && userInfo) {
        return JSON.parse(userInfo);
      }
      throw new Error('Não autenticado');
    }
  },

  usuariosLogados: {
    getAll: () => get('/api/UsuariosLogados'),
    putUsuarioLogado: (id) => put(`/api/UsuariosLogados/${id}`),
  },

  operadorLinha: {
    create: (data) => post('/api/OperadorLinha', data),
    obterDadosLinha: () => get('/api/OperadorLinha/obterDadosLinha'),
    liberar: () => del('/api/OperadorLinha'),
  },

  eventosLog: {
    getAll: (page = 1, pageSize = 20, filtroData = "", filtroUsuario = "") => {
      let url = `/api/Eventoslog?page=${page}&pageSize=${pageSize}`;
      if (filtroData) {
        url += `&data=${filtroData}`;
      }
      if (filtroUsuario) {
        url += `&usuario=${encodeURIComponent(filtroUsuario)}`;
      }
      return get(url);
    }
  },

  carro: {
    getTrem: (trainId) => get(`/api/telemetry/trem/${trainId}?limit=1000`),
    getDadosTrem: (trainId, page, pageSize) => get(`/api/telemetry/trem/${trainId}/paged?page=${page}&pageSize=${pageSize}`),
    getDadosGrafico: (trainSelected, paramId, range) => get(`/api/telemetry/trem/${trainSelected}/range?paramId=${paramId}&range=${range}`)
  },
  notifications: {
    getAll: (page = 1, pageSize = 10, filtros = {}) => {
      const params = new URLSearchParams();

      params.append("page", page);
      params.append("pageSize", pageSize);

      if (filtros.trem) params.append("trem", filtros.trem);
      if (filtros.parametro) params.append("parametro", filtros.parametro);
      if (filtros.carro) params.append("carro", filtros.carro);

      if (filtros.eventoInicio) params.append("eventoInicio", new Date(filtros.eventoInicio).toISOString());
      if (filtros.eventoFim) params.append("eventoFim", new Date(filtros.eventoFim).toISOString());

      if (filtros.cienteInicio) params.append("cienteInicio", new Date(filtros.cienteInicio).toISOString());
      if (filtros.cienteFim) params.append("cienteFim", new Date(filtros.cienteFim).toISOString());

      if (filtros.usuario) params.append("usuario", filtros.usuario);
      if (filtros.lidas !== undefined) params.append("lidas", filtros.lidas);
      if (filtros.naoLidas !== undefined) params.append("naoLidas", filtros.naoLidas);
      if (filtros.linhaId !== undefined) params.append("linhaId", filtros.linhaId);
      return get(`/api/notifications?${params.toString()}`);
    },

    markAsRead: (id) => put(`/api/notifications/read/${id}`),

    streamUrl: async () => {
      const config = await loadConfig();
      return `${config.API_URL}/api/alarmeStream`;
    }
  },
  trainDashboard: {
    streamUrl: async (trainId) => {
      const config = await loadConfig();
      return `${config.API_URL}/api/trainDashboardStream/${trainId}`;
    },

    // Fallback de polling do painel: passa por authenticatedFetch, então uma sessão
    // expirada redireciona para /login em vez de falhar em silêncio a cada ciclo.
    snapshot: (trainId) => get(`/api/trainDashboard/snapshot/${trainId}`)
  }
};

export default {
  authenticatedFetch,
  get,
  post,
  put,
  patch,
  del,
  api
};


